import { useNavigate } from 'react-router-dom';
import { theme } from '../../lib/theme';
import { Wrench, ImageIcon, Bell, List, FileText } from 'lucide-react';

interface SparePartItemProps {
    part: {
        id: string;
        name: string;
        manufacturer?: string;
        part_code?: string;
        price?: number;
        quantity?: number;
        comment?: string;
        created_at: string;
        image_count?: number;
        images?: any[];
        spare_part_images?: any[];
        reminders?: any[];
        entryCount?: number;
        noteCount?: number;
        derivedPrice?: number | null;
    };
    slug?: string;
    isAdminView?: boolean;
    compact?: boolean;
    onClick?: (e: React.MouseEvent) => void; // Optional override for click handler
    className?: string;
    highlighted?: boolean;
}

export function SparePartItem({ part, slug, isAdminView = false, compact = false, onClick, className = '', highlighted = false }: SparePartItemProps) {
    const navigate = useNavigate();

    const navigateToPart = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick) {
            onClick(e);
            return;
        }
        const prefix = isAdminView ? '/admin' : '';
        if (slug) {
            navigate(`${prefix}/${slug}/parts/${part.id}`);
        }
    };

    // Normalize image count
    const imgCount = part.image_count !== undefined
        ? part.image_count
        : (part.images?.length || part.spare_part_images?.length || 0);

    const getReminderStatus = () => {
        if (!part.reminders || part.reminders.length === 0) return null;
        const r = part.reminders[0];
        // Compare dates (ignore time)
        const remindDateStr = typeof r.remind_date === 'string' ? r.remind_date.split('T')[0] : '';
        const todayStr = new Date().toISOString().split('T')[0];
        const isPast = remindDateStr < todayStr;
        const isActive = r.is_active && (!isPast || r.repeat_yearly);
        return isActive ? 'active' : 'inactive';
    };
    const reminderStatus = getReminderStatus();

    return (
        <button
            type="button"
            onClick={navigateToPart}
            className={`w-full group text-left flex items-start gap-4 ${compact ? 'p-4' : theme.spacing.cardPadding} transition-all ${highlighted ? 'bg-yellow-50 dark:bg-yellow-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'} active:scale-[0.99] ${className}`}
        >
            <div className={`${theme.spacing.iconContainer} rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0`}>
                <Wrench size={20} className="text-orange-600 dark:text-orange-400" />
            </div>

            <div className="flex-1 min-w-0 flex items-start gap-3">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                        <h4 className={`${theme.typography.itemTitle} truncate`}>
                            {part.name}
                        </h4>
                        {part.manufacturer && (
                            <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded tracking-wide flex-shrink-0">
                                {part.manufacturer}
                            </span>
                        )}
                        {part.part_code && (
                            <span className="text-[10px] font-black text-gray-400 bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded uppercase tracking-widest flex-shrink-0">
                                {part.part_code}
                            </span>
                        )}
                    </div>

                    {part.comment && (
                        <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium leading-relaxed line-clamp-2 italic mt-0.5">
                            {part.comment}
                        </p>
                    )}
                </div>

                <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5">
                    {/* Price: own price takes priority, fallback to derived from most recent log entry */}
                    {(part.price || 0) > 0 ? (
                        <span className={`${theme.typography.itemTitle} whitespace-nowrap`}>
                            {(part.price || 0).toFixed(2)} €
                        </span>
                    ) : (part.derivedPrice || 0) > 0 ? (
                        <span className="text-[13px] font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            ~{(part.derivedPrice || 0).toFixed(2)} €
                        </span>
                    ) : (
                        <span className={`${theme.typography.itemTitle} whitespace-nowrap`}>&nbsp;</span>
                    )}

                    <div className="flex items-center gap-2">
                        {(part.entryCount || 0) > 0 && (
                            <span className="flex items-center gap-1 text-[13px] font-medium text-emerald-500 dark:text-emerald-400">
                                <List size={14} />
                                {part.entryCount}
                            </span>
                        )}
                        {(part.noteCount || 0) > 0 && (
                            <span className="flex items-center gap-1 text-[13px] font-medium text-purple-500 dark:text-purple-400">
                                <FileText size={14} />
                                {part.noteCount}
                            </span>
                        )}
                        {imgCount > 0 && (
                            <span className="flex items-center gap-1 text-[13px] font-medium text-blue-500 dark:text-blue-400">
                                <ImageIcon size={14} />
                                {imgCount}
                            </span>
                        )}
                        {reminderStatus === 'active' && (
                            <Bell size={14} className="text-blue-500" />
                        )}
                        {reminderStatus === 'inactive' && (
                            <Bell size={14} className="text-gray-400" />
                        )}
                    </div>
                </div>
            </div>
        </button>
    );
}
